import { useCallback } from 'react';
import { useCollapseAnimation } from './useCollapseAnimation';
import clsx from 'clsx';
import Icon from '../ui/Icon';

export interface SectionProps {
  title: string;
  children: React.ReactNode;
  isOpen: boolean;
  onToggle: (isOpen: boolean) => void;
  headerActions?: React.ReactNode;
  /** Show the expand/collapse chevron. Hidden when the section has no items. */
  collapsible?: boolean;
}

function Section({
  title,
  children,
  isOpen,
  onToggle,
  headerActions,
  collapsible = true,
}: SectionProps) {
  const { style, innerRef } = useCollapseAnimation(isOpen);

  const toggleOpen = useCallback(() => {
    onToggle(!isOpen);
  }, [isOpen, onToggle]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleOpen();
    }
  };

  return (
    <div className="side-nav-section">
      <div className="side-nav-section-header">
        <div
          className={clsx('side-nav-section-dropdown-trigger', isOpen && 'active')}
          onClick={toggleOpen}
          onKeyDown={handleKeyDown}
          role="button"
          tabIndex={0}
          aria-expanded={isOpen}
        >
          <span className="side-nav-section-title">
            <div className="side-nav-section-heading">{title}</div>
            {collapsible && <Icon name="arrow-down" className="side-nav-arrow" />}
          </span>
        </div>
        {headerActions && (
          <div
            className="side-nav-section-header-actions"
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(event) => event.stopPropagation()}
          >
            {headerActions}
          </div>
        )}
      </div>
      <div className={clsx('side-nav-section-content', isOpen && 'show')} style={style}>
        <div ref={innerRef}>{children}</div>
      </div>
    </div>
  );
}

export default Section;
